<template>
  <v-container fluid id="tract-contracts">
    <Dialog
      :stateId="dialogId"
      :enforcePersistence="requireEditApproval"
      maxWidth="80vw"
      @dialog-closing="resetDialogs"
    >
      <ContractEditor
        v-if="editing"
        :shouldAddActivity="shouldAddActivity"
        :contract="focusedContract"
        @contract-changed="save"
        @contract-changed-keep-open="contractChanged"
        @close="resetDialogs"
        :contractId="focusedContract.contractId"
        :contractMode="logContractMode"
        :companyRequiresEditApproval="companyInfo.requireApprovalForContractModifications"
        :currentDraftMode="focusedContract.isDraft"
      />
      <ContractDetail
        @edit-activities="editActivities"
        @edit-contract="editContract"
        v-if="contractDetail"
        :contractId="focusedContract.contractId"
        :hasPendingTickets="focusedContract ? focusedContract.hasPendingTickets : false"
        @review="refreshContracts"
      />
    </Dialog>
    <v-dialog v-model="deleting" width="400">
      <ConfirmDelete
        v-if="deleting"
        title="Contract"
        @delete="deleteItem"
        @cancel-delete="resetDialogs"/>
    </v-dialog>
    <ContractTable
    :contracts="contracts"
    :isTractDetail="true"
    :isByProducts="false"
    :tractId="tractId"
    :propFilter.sync="filter"
    :loading.sync="loading"
    @refresh-contracts="refreshContracts"
    @contract-action="parseTableAction"/>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ContractMode, ContractStatus, ContractType } from '@/utils/Enumerations'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'

export default {
  name: 'TractContracts',

  props: {
    tractId: {
      type: Number,
      required: true
    },
    refreshTractContracts: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    dialogId: 'tract-contracts',
    allContracts: [],
    contracts: [],
    loading: false,
    deleting: false,
    shouldAddActivity: false,
    contractDetail: false,
    editing: false,
    focusedContract: {},
    filter: {
      includeOpen: true,
      includeOnHold: false,
      includeClosed: false,
      includeExpired: false,
      limitToDraft: false,
      limitToPaused: false,
      limitToInternal: false,
      includeProduction: true,
      includeWoodsSale: true
    }
  }),

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    ContractTable: () => import('@/components/contract/ContractTable.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    ContractEditor: () => import('@/components/contract/ContractEditor.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue')
  },

  computed: {
    ...mapGetters('dialog', ['topDialogId']),
    ...mapGetters('user', ['companyInfo']),

    logContractMode () {
      return ContractMode.Logs
    },

    requireEditApproval () {
      return this.companyInfo.requireApprovalForContractModifications && !this.focusedContract.isDraft && this.editing
    }
  },

  watch: {
    refreshTractContracts: {
      handler (val) {
        if (val) {
          this.refreshContracts()
        }
      }
    },

    filter: {
      deep: true,
      handler (val) {
        localStorage.setItem(LocalStorageKeys.TRACT_CONTRACTS_TABLE_FILTER, JSON.stringify(val))
        this.applyFilter()
      }
    }
  },

  created () {
    const persisted = JSON.parse(localStorage.getItem(LocalStorageKeys.TRACT_CONTRACTS_TABLE_FILTER) ?? '{}')
    this.filter = { ...this.filter, ...persisted }
    this.refreshContracts()
  },

  methods: {
    ...mapActions('tract', ['fetchContractsForTract']),
    ...mapActions('contract', ['deleteContract']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async refreshContracts () {
      this.loading = true
      this.allContracts = await this.fetchContractsForTract(this.tractId)
      this.applyFilter()
      this.loading = false
      this.$emit('tract-contracts-refreshed')
    },

    applyFilter () {
      this.contracts = this.allContracts.filter(c => {
        const statusMatch = (
          (this.filter.includeOpen && c.status === ContractStatus.Open.value) ||
          (this.filter.includeClosed && c.status === ContractStatus.Closed.value) ||
          (this.filter.includeOnHold && c.status === ContractStatus.OnHold.value) ||
          (this.filter.includeExpired && c.status === ContractStatus.Expired.value)
        )

        const noTypeFilterActive = !this.filter.includeProduction && !this.filter.includeWoodsSale // if neither is selected, show all
        const typeMatch = noTypeFilterActive ||
          (this.filter.includeProduction && c.type === ContractType.Production.value) ||
          (this.filter.includeWoodsSale && c.type === ContractType.WoodsSale.value)

        const internalMatch = !this.filter.limitToInternal || c.isExternal !== true
        const draftMatch = !this.filter.limitToDraft || c.isDraft !== false
        const pausedMatch = !this.filter.limitToPaused || c.paused !== false

        return statusMatch && typeMatch && internalMatch && draftMatch && pausedMatch
      })
    },

    parseTableAction (action) {
      switch (action.actionType) {
        case 'edit-contract':
          this.editContract(action.contract)
          break
        case 'contract-detail':
          this.viewDetails(action.contract)
          break
        case 'delete-contract':
          this.viewConfirmDelete(action.contract)
          break
      }
    },

    viewDetails (contract) {
      this.closeSubdialogs()
      this.focusedContract = contract
      this.contractDetail = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80%' })
    },

    viewConfirmDelete (contract) {
      this.focusedContract = contract
      this.deleting = true
    },

    editContract (contract) {
      this.closeSubdialogs()
      this.focusedContract = contract
      this.openOrUpdateDialog({ id: this.dialogId, width: '80%' })
      this.editing = true
    },

    editActivities (contract) {
      this.closeSubdialogs()
      this.focusedContract = Object.assign({}, contract)
      this.shouldAddActivity = true
      this.editing = true
    },

    async deleteItem () {
      await this.deleteContract(this.focusedContract.contractId)
      this.resetDialogs()
      await this.refreshContracts()
      this.$emit('refresh-tract-destinations')
    },

    closeSubdialogs () {
      this.editing = false
      this.deleting = false
      this.contractDetail = false
    },

    resetDialogs () {
      this.closeSubdialogs()
      this.focusedContract = {}
      this.closeDialogsAtOrAbove('tract-contracts')
    },

    async save () {
      await this.refreshContracts()
      this.resetDialogs()
      await this.refreshContracts()
    },

    contractChanged () {
      this.refreshContracts()
    }
  }
}
</script>
